import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Space, Typography, message, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import AxiosOICStat from 'AxiosOICStat';

const { Option } = Select;
const { Text } = Typography;

export default function AddSdgMappingModal({ 
  visible, 
  onCancel, 
  onSubmit, 
  loading, 
  indicatorOptions,
  dimensionKeys = [],
  sdgSeries = []
}) {
  const [form] = Form.useForm();
  const [dimensions, setDimensions] = useState([]);
  const [dimensionOptions, setDimensionOptions] = useState({});
  const [loadingDimensions, setLoadingDimensions] = useState(false);

  useEffect(() => {
    if (dimensionKeys.length > 0) {
      fetchDimensionOptions();
    }
  }, [dimensionKeys]);

  const fetchDimensionOptions = async () => {
    if (dimensionKeys.length === 0) return;
    
    setLoadingDimensions(true);
    try {
      const response = await AxiosOICStat.post(`/external_api/sdg/get-sdg-dimension-options`, {
        dim_names: dimensionKeys
      });
    
      if (response.data) {
        setDimensionOptions(response.data);
      } else {
        message.error('Failed to fetch dimension options');
      }
    } catch (error) {
      console.error('Error fetching dimension options:', error);
      message.error('Error loading dimension options');
    } finally {
      setLoadingDimensions(false);
    }
  };

  const handleSeriesCodeChange = (seriesCode) => {
    const selectedSeries = sdgSeries.find(series => series.code === seriesCode);
    if (selectedSeries) {
      form.setFieldsValue({
        SeriesCode: selectedSeries.code,
        SeriesDescription: selectedSeries.description,
        Indicator: Array.isArray(selectedSeries.indicator) ? selectedSeries.indicator.join(', ') : selectedSeries.indicator
      });
    }
  };

  const handleAddDimension = () => {
    setDimensions([...dimensions, { key: '', value: '' }]);
  };

  const handleRemoveDimension = (index) => {
    const newDimensions = dimensions.filter((_, i) => i !== index);
    setDimensions(newDimensions);
  };

  const handleDimensionChange = (index, field, value) => {
    const newDimensions = [...dimensions];
    newDimensions[index] = { ...newDimensions[index], [field]: value };
    
    if (field === 'key') {
      newDimensions[index].value = '';
    }
    
    setDimensions(newDimensions);
  };

  const handleFinish = async (values) => {
    const formData = { ...values };
    
    dimensions.forEach(dimension => {
      if (dimension.key && dimension.value) {
        formData[dimension.key] = dimension.value;
      }
    });

    try {
      await onSubmit(formData);
      form.resetFields();
      setDimensions([]);
    } catch (error) {
      message.error('Failed to add SDG mapping');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setDimensions([]);
    onCancel();
  };

  const availableDimensions = dimensionKeys.filter(
    key => !dimensions.some(dimension => dimension.key === key)
  );

  const getOptionsForDimension = (dimensionKey) => {
    return dimensionOptions[dimensionKey] || [];
  };

  return (
    <Modal
      title="Add New SDG Mapping"
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText="Add Mapping"
      cancelText="Cancel"
      width={800}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Form.Item
          name="SeriesCode"
          label="Series Code"
          rules={[{ required: true, message: 'Please select series code' }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            placeholder="Select Series Code"
            onChange={handleSeriesCodeChange}
            filterOption={(input, option) => {
              const optionText = `${option.value} - ${sdgSeries.find(s => s.code === option.value)?.description || ''}`;
              return optionText.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {sdgSeries.map(series => (
              <Option key={series.code} value={series.code}>
                {series.code} - {series.description}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="SeriesDescription"
          label="Series Description"
          rules={[{ required: true, message: 'Please enter series description' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter series description" disabled />
        </Form.Item>

        <Form.Item
          name="Indicator"
          label="SDG Indicator"
        >
          <Input placeholder="e.g., 3.1.1" disabled />
        </Form.Item>

        <Form.Item
          name="ind_code"
          label="Indicator Code"
          rules={[{ required: true, message: 'Please select indicator' }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            placeholder="Select Indicator"
            options={indicatorOptions}
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <div style={{ marginBottom: 16 }}>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong>Dimensions</Text>
            <Button
              type="dashed"
              onClick={handleAddDimension}
              icon={<PlusOutlined />}
              size="small"
              disabled={availableDimensions.length === 0}
            >
              Add Dimension
            </Button>
            {loadingDimensions && <Spin size="small" />}
          </Space>

          {dimensions.map((dimension, index) => (
            <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
              <Select
                placeholder="Select dimension"
                value={dimension.key}
                onChange={(value) => handleDimensionChange(index, 'key', value)}
                style={{ width: 250 }}
              >
                {availableDimensions.concat(dimension.key ? [dimension.key] : []).map(key => (
                  <Option key={key} value={key}>
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Option>
                ))}
              </Select>
              
              <Select
                placeholder="Select value"
                value={dimension.value}
                onChange={(value) => handleDimensionChange(index, 'value', value)}
                style={{ width: 300 }}
                showSearch
                disabled={!dimension.key}
                loading={loadingDimensions}
                filterOption={(input, option) =>
                  String(option.value).toLowerCase().includes(input.toLowerCase())
                }
              >
                {getOptionsForDimension(dimension.key).map(option => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </Select>
              
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveDimension(index)}
              />
            </Space>
          ))}

          {dimensions.length === 0 && (
            <Text type="secondary">No dimensions added yet</Text>
          )}
        </div>
      </Form>
    </Modal>
  );
}